// Per-CLUB page (Josh 8/13): every film club gets its own shareable home —
// /film-clubs/club/scream-scene — listing that club's upcoming screenings.
// Lives IN ADDITION to the per-event pages: the event link shares one night,
// this page is the club's standing calendar on the site (the promoter
// flywheel: clubs share their own page with their followers).
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import supabase from '../supabase';
import { FILM_CLUB_FILTER, eventUrlPath, clubName, clubSlug } from '../utils/filmClubUrls';

const GOLD = '#8b6914';
const GOLD_LIGHT = '#d4a843';
const INK = '#2a2520';

export default function FilmClub() {
  const { clubSlug: slugParam } = useParams();
  const [events, setEvents] = useState([]);
  const [movies, setMovies] = useState({});
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from('showtimes')
          .select('*')
          .or(FILM_CLUB_FILTER)
          .gte('start_at', new Date().toISOString())
          .order('start_at');
        const rows = (data || []).filter((r) => clubSlug(r.series) === slugParam);
        setEvents(rows);
        const ids = [...new Set(rows.map((r) => String(r.movie_id)))];
        if (ids.length) {
          const { data: mv } = await supabase.from('movies').select('id, title, slug, release_year, poster_path').in('id', ids);
          setMovies(Object.fromEntries((mv || []).map((m) => [String(m.id), m])));
        }
      } catch (e) { /* empty state below */ }
      setLoading(false);
    })();
  }, [slugParam]);

  const displayName = events.length
    ? clubName(events[0].series)
    : slugParam.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const share = async () => {
    const url = window.location.origin + `/film-clubs/club/${slugParam}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${displayName} — Film Revival NYC`, url }); return; } catch (e) { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { window.prompt('Copy this link:', url); }
  };

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '28px 16px 40px', color: INK }}>
      <a href="/film-clubs" style={{ color: GOLD, textDecoration: 'none', fontSize: '0.85rem' }}>← All clubs &amp; community screenings</a>

      <div style={{ textAlign: 'center', margin: '20px 0 26px' }}>
        <div style={{ fontFamily: 'Cinzel, serif', fontSize: '0.75rem', letterSpacing: '3px', textTransform: 'uppercase', color: GOLD }}>Screening Organizer</div>
        <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.7rem', letterSpacing: '1px', margin: '8px 0 6px' }}>{displayName}</h1>
        <div style={{ width: '64px', height: '2px', background: GOLD_LIGHT, margin: '0 auto 4px' }} />
        <div style={{ width: '32px', height: '2px', background: GOLD_LIGHT, margin: '0 auto 14px' }} />
        <button type="button" onClick={share}
          style={{
            padding: '7px 20px', background: 'none', border: `1px solid ${GOLD}`, color: GOLD,
            borderRadius: '5px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
          }}>
          {copied ? 'Link copied!' : 'Share this organizer'}
        </button>
      </div>

      {loading ? (
        <p style={{ color: '#6b6255', textAlign: 'center' }}>Loading…</p>
      ) : events.length === 0 ? (
        <div style={{ background: '#faf7f2', border: '1px solid #d4cec4', borderRadius: '8px', padding: '24px', textAlign: 'center' }}>
          <p style={{ margin: 0, color: '#6b6255' }}>No upcoming screenings listed for this organizer — check back soon.</p>
        </div>
      ) : (
        events.map((ev) => {
          const mv = movies[String(ev.movie_id)];
          const start = new Date(ev.start_at);
          const dateStr = start.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric' });
          const timeStr = start.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
          const title = mv && mv.title ? `${mv.title}${mv.release_year ? ` (${mv.release_year})` : ''}` : ev.cinema_movie_title;
          const subSeries = ev.series && ev.series.includes(':') ? ev.series.split(':').slice(1).join(':').trim() : null;
          return (
            <div key={ev.id} style={{
              display: 'flex', gap: '14px', padding: '14px', marginBottom: '12px',
              background: '#fdfbf7', border: '1px solid #e5e0d8', borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(42,37,32,0.06)',
            }}>
              {mv && mv.poster_path ? (
                <img src={mv.poster_path} alt="" style={{ width: '72px', height: '108px', objectFit: 'cover', borderRadius: '4px', alignSelf: 'flex-start', boxShadow: '0 1px 4px rgba(42,37,32,0.25)' }} />
              ) : (
                <div style={{ width: '72px', height: '108px', borderRadius: '4px', background: '#1a1714', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', boxSizing: 'border-box', textAlign: 'center', flexShrink: 0, alignSelf: 'flex-start' }}>
                  <span style={{ fontFamily: 'Cinzel, serif', color: '#d4a843', fontSize: '0.55rem', lineHeight: 1.35, overflow: 'hidden' }}>{title}</span>
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                {subSeries && (
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: GOLD, marginBottom: '4px' }}>{subSeries}</div>
                )}
                <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.25 }}>
                  <a href={eventUrlPath(ev.id)} style={{ color: INK, textDecoration: 'none' }}>{title}</a>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b6255', marginTop: '4px' }}>
                  <strong style={{ color: INK }}>{dateStr}</strong> · {timeStr} · {ev.theater}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {ev.booking_link && (
                    <a href={ev.booking_link} target="_blank" rel="noopener noreferrer"
                      style={{
                        display: 'inline-block', marginTop: '10px', padding: '6px 16px',
                        background: GOLD, color: '#fff', borderRadius: '4px', textDecoration: 'none',
                        fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.03em',
                      }}>
                      Tickets
                    </a>
                  )}
                  <a href={eventUrlPath(ev.id)}
                    style={{
                      display: 'inline-block', marginTop: '10px', padding: '6px 16px', background: 'none',
                      border: `1px solid ${GOLD}`, color: GOLD, borderRadius: '4px', textDecoration: 'none',
                      fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.03em',
                    }}>
                    Event page
                  </a>
                </div>
              </div>
            </div>
          );
        })
      )}

      <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '0.8rem', color: '#8a8272' }}>
        <p style={{ margin: 0 }}>
          Repertory screenings from NYC's film clubs, libraries, and community venues —{' '}
          <a href="/film-clubs" style={{ color: GOLD }}>see the full calendar</a>.
        </p>
      </div>
    </div>
  );
}
